/**
 * Notices saved on this device. The service worker (public/sw.js, push handler)
 * writes each push it shows; the page only reads them here, so the notice sheet
 * never asks the network for the words of a notification. Newest first, the
 * newest 30 kept by the worker.
 */

import { createStore, del, get } from 'idb-keyval';

export interface SavedNotice {
  id: string;
  title: string;
  body: string;
  /** A longer text a sender may provide; shown instead of `body`. */
  detail?: string;
  url: string;
  tag?: string;
  /** Arrival time, epoch milliseconds. */
  at: number;
}

// Same database, store and key public/sw.js writes.
const noticeStore = createStore('jarvis-notices', 'kv');
const NOTICE_KEY = 'notices';
const NOTICE_ID = /^[A-Za-z0-9-]{8,64}$/;

export async function loadNotices(): Promise<SavedNotice[]> {
  const saved = await get<SavedNotice[]>(NOTICE_KEY, noticeStore);
  return Array.isArray(saved) ? saved : [];
}

/** The saved notice with this id, or null when it is gone or the id is malformed. */
export async function loadNotice(id: string): Promise<SavedNotice | null> {
  if (!NOTICE_ID.test(id)) return null;
  try {
    return (await loadNotices()).find((notice) => notice.id === id) ?? null;
  } catch (err) {
    console.warn('[push] could not read saved notices:', err);
    return null;
  }
}

/** Sign-out: the words of past notifications do not stay on the device. */
export async function clearNotices(): Promise<void> {
  try {
    await del(NOTICE_KEY, noticeStore);
  } catch (err) {
    console.warn('[push] could not clear saved notices:', err);
  }
}

/** Only a same-site path is followed; anything else goes to Today. */
export function safeNoticeLink(link: unknown): string {
  return typeof link === 'string' && /^\/(?![/\\])[^\\\s]*$/.test(link) ? link : '/today';
}
